import { openDB, DBSchema, IDBPDatabase } from 'idb';

interface HMPosDB extends DBSchema {
  orders: {
    key: string;
    value: {
      _id: string;
      orderNumber: string;
      customerName: string;
      totalAmount: number;
      status: string;
      createdAt: string;
      synced: boolean;
      syncStatus: 'pending' | 'synced' | 'conflict';
      data: any;
    };
    indexes: {
      'by-syncStatus': string;
      'by-createdAt': string;
    };
  };
  customers: {
    key: string;
    value: {
      _id: string;
      firstName: string;
      lastName: string;
      email: string;
      phone: string;
      synced: boolean;
      syncStatus: 'pending' | 'synced' | 'conflict';
      data: any;
    };
    indexes: {
      'by-syncStatus': string;
    };
  };
  services: {
    key: string;
    value: {
      _id: string;
      name: string;
      price: number;
      synced: boolean;
      data: any;
    };
  };
  inventory: {
    key: string;
    value: {
      _id: string;
      name: string;
      quantity: number;
      synced: boolean;
      data: any;
    };
  };
  syncQueue: {
    key: string;
    value: {
      id: string;
      method: 'POST' | 'PUT' | 'DELETE';
      endpoint: string;
      data: any;
      timestamp: number;
      retries: number;
    };
  };
}

const DB_NAME = 'HMPosDB';
const DB_VERSION = 1;

let db: IDBPDatabase<HMPosDB> | null = null;

export const initDB = async () => {
  if (db) return db;

  db = await openDB<HMPosDB>(DB_NAME, DB_VERSION, {
    upgrade(database) {
      // Orders store
      if (!database.objectStoreNames.contains('orders')) {
        const orderStore = database.createObjectStore('orders', { keyPath: '_id' });
        orderStore.createIndex('by-syncStatus', 'syncStatus');
        orderStore.createIndex('by-createdAt', 'createdAt');
      }

      // Customers store
      if (!database.objectStoreNames.contains('customers')) {
        const customerStore = database.createObjectStore('customers', { keyPath: '_id' });
        customerStore.createIndex('by-syncStatus', 'syncStatus');
      }

      // Services store
      if (!database.objectStoreNames.contains('services')) {
        database.createObjectStore('services', { keyPath: '_id' });
      }

      // Inventory store
      if (!database.objectStoreNames.contains('inventory')) {
        database.createObjectStore('inventory', { keyPath: '_id' });
      }

      // Sync queue store
      if (!database.objectStoreNames.contains('syncQueue')) {
        database.createObjectStore('syncQueue', { keyPath: 'id' });
      }
    },
  });

  return db;
};

export const offlineStorage = {
  // Orders
  async saveOrder(order: any) {
    const database = await initDB();
    const orderToSave = {
      ...order,
      synced: false,
      syncStatus: 'pending' as const,
    };
    await database.put('orders', orderToSave);
  },

  async getOrders() {
    const database = await initDB();
    return database.getAll('orders');
  },

  async getOrder(id: string) {
    const database = await initDB();
    return database.get('orders', id);
  },

  async updateOrderSyncStatus(id: string, syncStatus: 'synced' | 'conflict') {
    const database = await initDB();
    const order = await database.get('orders', id);
    if (order) {
      order.synced = true;
      order.syncStatus = syncStatus;
      await database.put('orders', order);
    }
  },

  async getPendingOrders() {
    const database = await initDB();
    return database.getAllFromIndex('orders', 'by-syncStatus', 'pending');
  },

  // Customers
  async saveCustomer(customer: any) {
    const database = await initDB();
    const customerToSave = {
      ...customer,
      synced: false,
      syncStatus: 'pending' as const,
    };
    await database.put('customers', customerToSave);
  },

  async getCustomers() {
    const database = await initDB();
    return database.getAll('customers');
  },

  async getCustomer(id: string) {
    const database = await initDB();
    return database.get('customers', id);
  },

  async updateCustomerSyncStatus(id: string, syncStatus: 'synced' | 'conflict') {
    const database = await initDB();
    const customer = await database.get('customers', id);
    if (customer) {
      customer.synced = true;
      customer.syncStatus = syncStatus;
      await database.put('customers', customer);
    }
  },

  async getPendingCustomers() {
    const database = await initDB();
    return database.getAllFromIndex('customers', 'by-syncStatus', 'pending');
  },

  // Services (read-only cache from server)
  async saveServices(services: any[]) {
    const database = await initDB();
    const tx = database.transaction('services', 'readwrite');
    await Promise.all(services.map(service => tx.store.put({ ...service, synced: true })));
    await tx.done;
  },

  async getServices() {
    const database = await initDB();
    return database.getAll('services');
  },

  // Inventory (read-only cache from server)
  async saveInventory(items: any[]) {
    const database = await initDB();
    const tx = database.transaction('inventory', 'readwrite');
    await Promise.all(items.map(item => tx.store.put({ ...item, synced: true })));
    await tx.done;
  },

  async getInventory() {
    const database = await initDB();
    return database.getAll('inventory');
  },

  // Sync Queue
  async addToSyncQueue(item: { id: string; method: 'POST' | 'PUT' | 'DELETE'; endpoint: string; data: any }) {
    const database = await initDB();
    await database.put('syncQueue', {
      ...item,
      timestamp: Date.now(),
      retries: 0,
    });
  },

  async getSyncQueue() {
    const database = await initDB();
    return database.getAll('syncQueue');
  },

  async removeFromSyncQueue(id: string) {
    const database = await initDB();
    await database.delete('syncQueue', id);
  },

  async incrementSyncQueueRetries(id: string) {
    const database = await initDB();
    const item = await database.get('syncQueue', id);
    if (item) {
      item.retries++;
      await database.put('syncQueue', item);
    }
  },

  // Clear all data (for logout/reset)
  async clearAll() {
    const database = await initDB();
    const tx = database.transaction(['orders', 'customers', 'services', 'inventory', 'syncQueue'], 'readwrite');
    await Promise.all([
      tx.objectStore('orders').clear(),
      tx.objectStore('customers').clear(),
      tx.objectStore('services').clear(),
      tx.objectStore('inventory').clear(),
      tx.objectStore('syncQueue').clear(),
    ]);
    await tx.done;
  },
};
